<template>
  <div class="family-settings family-page bg-slate-50">
    <header
      class="family-page-header bg-white/90 backdrop-blur-md border-b border-slate-100"
      :style="{ paddingTop: 'env(safe-area-inset-top)' }"
    >
      <div class="px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          class="w-9 h-9 rounded-full bg-slate-50 flex items-center justify-center text-slate-500"
          @click="goBack"
        >
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 class="text-base font-bold text-slate-900">账号与安全</h1>
      </div>
    </header>

    <main class="family-page-body px-4 py-4 pb-28">
      <div class="bg-white rounded-xl overflow-hidden shadow-[0_2px_12px_rgb(0,0,0,0.04)] mb-3">
        <div
          class="h-28 bg-cover bg-center relative"
          :style="profileBgStyle"
        >
          <button
            type="button"
            class="absolute right-3 bottom-3 z-20 px-3 py-1.5 rounded-full bg-black/50 text-white text-xs font-medium active:bg-black/60"
            @click="triggerBgPick"
          >
            更换背景
          </button>
          <input ref="bgInput" type="file" accept="image/*" class="hidden" @change="onBgPick" />
        </div>
        <div class="px-4 pb-4 -mt-8 relative z-10 pointer-events-none">
          <label class="block w-16 h-16 rounded-2xl overflow-hidden border-2 border-white shadow bg-family-blue cursor-pointer pointer-events-auto">
            <img v-if="form.avatar" :src="form.avatar" alt="" class="w-full h-full object-cover" />
            <span v-else class="w-full h-full flex items-center justify-center text-white text-xl font-bold">
              {{ avatarLetter }}
            </span>
            <input type="file" accept="image/*" class="hidden" @change="onAvatarPick" />
          </label>
          <p class="text-xs text-slate-400 mt-2 pointer-events-none">点击头像可更换</p>
        </div>
      </div>

      <div class="bg-white rounded-xl p-4 mb-3 shadow-[0_2px_12px_rgb(0,0,0,0.04)] space-y-4">
        <label class="block">
          <span class="text-sm text-slate-600">昵称</span>
          <input v-model.trim="form.nickname" type="text" maxlength="50" class="family-field" placeholder="请输入昵称" />
        </label>
        <label class="block">
          <span class="text-sm text-slate-600">账号 ID（手机号）</span>
          <input :value="form.mobileMasked || form.mobile" type="text" disabled class="family-field bg-slate-50 text-slate-400" />
        </label>
        <button
          type="button"
          class="w-full py-3 rounded-xl bg-family-blue text-white text-sm font-semibold disabled:opacity-60"
          :disabled="savingProfile"
          @click="saveProfile"
        >
          {{ savingProfile ? "保存中..." : "保存资料" }}
        </button>
      </div>

      <div class="bg-white rounded-xl p-4 mb-3 shadow-[0_2px_12px_rgb(0,0,0,0.04)] space-y-4">
        <h3 class="text-sm font-bold text-slate-800">修改密码</h3>
        <label class="block">
          <span class="text-sm text-slate-600">原密码</span>
          <input v-model="pwd.oldPassword" type="password" class="family-field" placeholder="请输入原密码" />
        </label>
        <label class="block">
          <span class="text-sm text-slate-600">新密码</span>
          <input v-model="pwd.newPassword" type="password" class="family-field" placeholder="6-20 位新密码" />
        </label>
        <button
          type="button"
          class="w-full py-3 rounded-xl border border-family-blue text-family-blue text-sm font-semibold disabled:opacity-60"
          :disabled="savingPwd"
          @click="savePassword"
        >
          {{ savingPwd ? "提交中..." : "修改密码" }}
        </button>
      </div>

      <button
        type="button"
        class="w-full py-3 rounded-xl bg-red-50 text-red-500 text-sm font-semibold"
        @click="logoutVisible = true"
      >
        退出登录
      </button>
    </main>

    <FamilyMobileConfirm
      :visible.sync="logoutVisible"
      title="退出登录"
      message="确定要退出当前账号吗？"
      confirm-text="确认退出"
      cancel-text="取消"
      :danger="true"
      :loading="logoutLoading"
      @confirm="confirmLogout"
    />
  </div>
</template>

<script>
import { familyAuth } from "./familyAuth";
import { familyUserApi } from "./familyUserApi";
import { familyGoBack } from "./familyNavigation";
import FamilyMobileConfirm from "./FamilyMobileConfirm.vue";

export default {
  name: "FamilyUserSettings",
  components: { FamilyMobileConfirm },
  data() {
    return {
      loading: false,
      savingProfile: false,
      savingPwd: false,
      logoutVisible: false,
      logoutLoading: false,
      form: {
        nickname: "",
        mobile: "",
        mobileMasked: "",
        avatar: "",
        profileBg: "",
      },
      pwd: {
        oldPassword: "",
        newPassword: "",
      },
    };
  },
  computed: {
    avatarLetter() {
      return (this.form.nickname || "用").charAt(0);
    },
    profileBgStyle() {
      if (this.form.profileBg) {
        return { backgroundImage: `url(${this.form.profileBg})` };
      }
      return { background: "linear-gradient(135deg, #0082d4 0%, #006bb3 100%)" };
    },
  },
  mounted() {
    document.title = "账号与安全 - 安心陪护";
    document.documentElement.classList.add("family-route");
    document.body.classList.add("family-route");
    this.loadProfile();
  },
  beforeDestroy() {
    document.documentElement.classList.remove("family-route");
    document.body.classList.remove("family-route");
  },
  methods: {
    goBack() {
      familyGoBack(this.$router, this.$route, "profile");
    },
    triggerBgPick() {
      const input = this.$refs.bgInput;
      if (input) input.click();
    },
    async loadProfile() {
      this.loading = true;
      try {
        const data = await familyUserApi.profile();
        this.form = {
          nickname: data.nickname || "",
          mobile: data.mobile || "",
          mobileMasked: data.mobileMasked || data.mobile || "",
          avatar: data.avatar || "",
          profileBg: data.profileBg || "",
        };
      } catch (e) {
        const user = familyAuth.getUser();
        if (user) {
          this.form.nickname = user.nickname || "";
          this.form.mobile = user.mobile || "";
          this.form.mobileMasked = user.mobileMasked || "";
          this.form.avatar = user.avatar || "";
          this.form.profileBg = user.profileBg || "";
        }
        this.$message.error((e && e.message) || "加载资料失败");
      } finally {
        this.loading = false;
      }
    },
    async onAvatarPick(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;
      try {
        const { url } = await familyUserApi.upload(file);
        this.form.avatar = url;
        this.$message.success("头像已上传，记得保存资料");
      } catch (err) {
        this.$message.error((err && err.message) || "头像上传失败");
      }
    },
    async onBgPick(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;
      try {
        const { url } = await familyUserApi.upload(file);
        this.form.profileBg = url;
        this.$message.success("背景已上传，记得保存资料");
      } catch (err) {
        this.$message.error((err && err.message) || "背景上传失败");
      }
    },
    async saveProfile() {
      if (!this.form.nickname) {
        this.$message.warning("请填写昵称");
        return;
      }
      this.savingProfile = true;
      try {
        const data = await familyUserApi.updateProfile({
          nickname: this.form.nickname,
          avatar: this.form.avatar,
          profileBg: this.form.profileBg,
        });
        familyAuth.patchLocalUser({
          nickname: data.nickname,
          avatar: data.avatar,
          profileBg: data.profileBg,
          mobileMasked: data.mobileMasked,
        });
        this.$message.success("资料已保存");
      } catch (e) {
        this.$message.error((e && e.message) || "保存失败");
      } finally {
        this.savingProfile = false;
      }
    },
    async savePassword() {
      if (!this.pwd.oldPassword || !this.pwd.newPassword) {
        this.$message.warning("请填写原密码和新密码");
        return;
      }
      this.savingPwd = true;
      try {
        await familyUserApi.updatePassword(this.pwd);
        this.pwd = { oldPassword: "", newPassword: "" };
        this.$message.success("密码修改成功");
      } catch (e) {
        this.$message.error((e && e.message) || "修改失败");
      } finally {
        this.savingPwd = false;
      }
    },
    confirmLogout() {
      this.logoutLoading = true;
      familyAuth.logout();
      this.logoutVisible = false;
      this.logoutLoading = false;
      this.$router.replace("/family/login");
    },
  },
};
</script>

<style scoped>
.family-field {
  margin-top: 0.375rem;
  width: 100%;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background-color: #f1f5f9;
  font-size: 0.875rem;
  color: #1e293b;
  border: 1px solid transparent;
}
.family-field:focus {
  outline: none;
  border-color: #0082d4;
  background-color: #fff;
}
</style>
